/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { ChangeEvent, FC, Fragment, useContext, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTheme } from 'emotion-theming';
import { useMutation } from 'react-query';
import { appStore } from '../../../state';
import { Basket, Button, Checkbox, Form, Input, List, Page, Text, VoucherInfo } from '../..';
import { Copy, ROUTES, ThemeProps } from '../../../types';
import {
  isEmpty,
  isValidEmail,
  pricePresenter,
  textReplacer,
} from '../../../utils';
import { postUserSignin, postUserSignup } from '../../../api';
import { useGetBillingStatus } from '../../../hooks';

interface UserRegistrationProps extends Copy { }

const UserRegistration: FC<UserRegistrationProps> = ({
  fallbackCopy,
  localeCopy,
}) => {
  const history = useHistory();
  const { dispatch, state } = useContext(appStore);
  const { form, skipPayment, user, config } = state;
  const { currencySymbols } = config;
  const theme: ThemeProps = useTheme();
  // initialise form
  const { control, errors, handleSubmit, reset } = useForm({
    criteriaMode: 'all',
    defaultValues: {
      over18: form.over18 || false,
      emailAddress: form.emailAddress || '',
      marketingAccepted: form.marketingAccepted || false,
      password: form.password || '',
      termsAccepted: form.termsAccepted || false,
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>, handler: any) => {
    handler(event.target.checked);
  };

  const billingStatusQuery = useGetBillingStatus({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
  });

  const renderMessage = ({
    errors,
    messages,
  }: {
    errors: any;
    messages: Record<string, string>;
  }) => {
    const {
      eightChars,
      oneLowerCaseLetter,
      oneNumber,
      oneUpperCaseLetter,
      text,
    } = messages;
    const requirementsArray = [
      eightChars,
      oneUpperCaseLetter,
      oneLowerCaseLetter,
      oneNumber,
    ];
    const errorsArray: any = Object.values(errors || []).reduce(
      (acc: any, curr: any) => {
        return [...acc, curr];
      },
      []
    );

    return (
      <div>
        <Text
          as='span'
          color={!isEmpty(errors) ? 'error' : 'muted'}
          weight='light'>
          {text}
          {requirementsArray.map((requirement: string, index: number) => (
            <Text
              as='span'
              color={errorsArray.includes(requirement) ? 'error' : 'muted'}
              key={index}
              weight='light'>
              {requirement}
            </Text>
          ))}
        </Text>
      </div>
    );
  };

  const {
    isLoading: userSignupIsLoading,
    mutateAsync: postUserRegistration,
  } = useMutation(postUserSignup, {
    onError: (error: any) => {
      console.error('Error with posting user registration, ', error);
      error.then((err: any) => dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          dismissable: true,
          id: new Date().getTime(),
          text:
            err.status === 409
              ? (localeCopy?.errors?.USER_ALREADY_REGISTERED ||
                fallbackCopy.errors.USER_ALREADY_REGISTERED)
              : (localeCopy?.errors?.GENERIC_ERROR ||
                fallbackCopy.errors.GENERIC_ERROR),
          timeout: 7,
          type: 'error',
        },
      }))

    },
    onSuccess: (data) => {
      /**
       * This `login` is an 'old' endpoint. When an invalid password is POSTed, it returns
       * a 200 with the error in the body of the response. We will throw it here and
       * handle the error in the `catch` below
       */
      if (data.error === true) {
        throw data;
      }
      /**
       * Save response to state
       */
      dispatch({
        type: 'UPDATE_USER',
        payload: { ...data },
      });
    },
  });

  const {
    isLoading: userSigninIsLoading,
    mutateAsync: postUserSigninData,
  } = useMutation(postUserSignin, {
    onSuccess: ({ data, resHeaders }) => {
      /**
       * This `login` is an 'old' endpoint. When an invalid password is POSTed, it returns
       * a 200 with the error in the body of the response. We will throw it here and
       * handle the error in the `catch` below
       */
      if (data.error === true) {
        throw data;
      }
      /**
       * Response headers are stripped by default for security. We need to use a getter
       * to explicitly get the headers to set countryCode:
       * https://stackoverflow.com/a/44816592/9935125
       */
      const countryCode = resHeaders.get('x-az-countrycode');
      const { apiKey, apiSecret, id } = data;
      const cdnExpiry = data['cdn-expiry'];
      const cdnSessionId = data['cdn-sessionid'];

      dispatch({
        type: 'UPDATE_USER',
        payload: {
          apiKey,
          apiSecret,
          countryCode,
          id,
          cdnExpiry,
          cdnSessionId,
        },
      });

      /**
       * Reset password fields
       */
      reset({
        password: '',
      });

      /**
       * Navigate to the next page when
       */
      if (!skipPayment.skip) {
        history.push(ROUTES.PAYMENT);
      }
    },
  });

  /**
   * Form submit success handler where we let react-hook-form validate, then we dispatch our state action to save selected data in state and persist in
   * localStorage.
   */
  const onSubmit = async (formData: any) => {
    try {
      const {
        emailAddress,
        marketingAccepted,
        over18,
        password,
        termsAccepted,
      } = formData;
      /**
       * Save form data to state
       */
      dispatch({
        type: 'UPDATE_FORM_DATA',
        payload: {
          emailAddress,
          marketingAccepted,
          over18,
          termsAccepted,
        },
      });

      /**
       * post registration data to APi
       */
      const res = await postUserRegistration({
        emailAddress,
        marketingAccepted,
        over18,
        password,
        pinNumber: '1234',
        source: 'WEBSITE_BILLING',
        termsAccepted,
      });

      if (res) {
        /**
         * Now log user in
         */
        await postUserSigninData({
          password,
          userName: emailAddress,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    return () => {
      if (skipPayment?.skip && !!user.id) {
        dispatch({
          type: 'UPDATE_SKIP_PAYMENT',
          payload: {
            skip: false,
            planId: 1,
          },
        });
        billingStatusQuery.refetch()
          .then(() => history.push(ROUTES.PAYMENT_SUCCESS));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  /**
   * Form submit error handler
   */
  const onError = (errors: any) => {
    console.error('Form errors: ', errors);
  };

  const { selectedPlan } = form;
  const { billingCycle, currency, totalPrice, discounts } = selectedPlan!;

  return (
    <Page
      isReady={true}
      pageNumber={2}
      title={localeCopy?.title || fallbackCopy?.title}>
      {!isEmpty(selectedPlan) && (
        skipPayment.skip ?
          (
            <VoucherInfo
              header={localeCopy?.components?.basket?.prepayVoucherAppliedHeader ||
                fallbackCopy.components.basket.prepayVoucherAppliedHeader}
              details={textReplacer(
                localeCopy?.components?.basket?.prepayVoucherAppliedDetails ||
                fallbackCopy.components.basket.prepayVoucherAppliedDetails,
                {
                  '{VOUCHER_DURATION}': selectedPlan?.discounts[0].durationInMonths,
                  '{MONTH}': discounts[0].durationInMonths === 1 ? (
                    localeCopy?.month?.singular || fallbackCopy.month.singular) :
                    (localeCopy?.month?.plural || fallbackCopy.month.plural)
                }
              )}
            />
          )
          : (
            <Basket
              {...selectedPlan}
              priceFrequency={
                localeCopy?.plans[billingCycle.toLowerCase()]?.priceFrequency ||
                fallbackCopy.plans[billingCycle.toLowerCase()].priceFrequency
              }
              title={
                localeCopy?.plans[billingCycle.toLowerCase()]?.title ||
                fallbackCopy.plans[billingCycle.toLowerCase()].title
              }
              currencySymbols={currencySymbols}
              accentMessage={(discounts?.length !== 0) ?
                ((discounts[0]?.percentage < 100) ?
                  (textReplacer(
                    localeCopy?.plans[billingCycle.toLowerCase()]?.accentSecondaryMessage ||
                    fallbackCopy.plans[billingCycle.toLowerCase()].accentSecondaryMessage,
                    {
                      '{VOUCHER_PERCENTAGE}': discounts[0].percentage,
                      '{VOUCHER_DURATION}': discounts[0].durationInMonths,
                      '{MONTH}': discounts[0].durationInMonths === 1 ? (
                        localeCopy?.month?.singular || fallbackCopy.month.singular) :
                        (localeCopy?.month?.plural || fallbackCopy.month.plural)
                    })
                  ) :
                  (
                    (textReplacer(
                      localeCopy?.plans[billingCycle.toLowerCase()]?.accentTertiaryMessage ||
                      fallbackCopy.plans[billingCycle.toLowerCase()].accentTertiaryMessage,
                      {
                        '{VOUCHER_PERCENTAGE}': discounts[0].percentage,
                        '{VOUCHER_DURATION}': discounts[0].durationInMonths,
                        '{MONTH}': discounts[0].durationInMonths === 1 ? (
                          localeCopy?.month?.singular || fallbackCopy.month.singular) :
                          (localeCopy?.month?.plural || fallbackCopy.month.plural)
                      })
                    )
                  )
                ) :
                (
                  localeCopy?.plans[billingCycle.toLowerCase()]?.accentMessage ||
                  fallbackCopy.plans[billingCycle.toLowerCase()].accentMessage
                )
              }
              detailsMessage={textReplacer(
                localeCopy?.plans[billingCycle.toLowerCase()]?.detailsMessage ||
                fallbackCopy.plans[billingCycle.toLowerCase()].detailsMessage,
                {
                  '{PRICE}': pricePresenter(totalPrice, currencySymbols, currency)
                }
              )
              }
              additionalInfo={
                localeCopy?.plans[billingCycle.toLowerCase()]?.additionalInfo ||
                fallbackCopy.plans[billingCycle.toLowerCase()].additionalInfo
              }
              details={(discounts?.length !== 0 && discounts[0]?.percentage < 100) ?
                localeCopy?.plans[billingCycle.toLowerCase()]?.details ||
                fallbackCopy.plans[billingCycle.toLowerCase()].details
                :
                undefined
              }
            />
          ))}
      <Link
        css={{ alignSelf: 'flex-start', fontSize: 12, marginBottom: 20 }}
        to={ROUTES.USER_LOGIN}>
        <Text>{localeCopy?.loginInstead || fallbackCopy.loginInstead}</Text>
      </Link>
      <Form
        disableEnterKeypress={true}
        onSubmit={handleSubmit(onSubmit, onError)}>
        <Controller
          control={control}
          name='emailAddress'
          render={({ name, onChange, value }) => (
            <Input
              label={
                localeCopy?.emailAddress?.label ||
                fallbackCopy.emailAddress.label
              }
              errorMessage={errors && errors?.emailAddress?.message}
              name={name}
              onChange={onChange}
              placeholder={
                localeCopy?.emailAddress?.placeholder ||
                fallbackCopy.emailAddress.placeholder
              }
              type='email'
              value={value}
            />
          )}
          rules={{
            required: {
              message:
                localeCopy?.emailAddress?.errorMessage?.required ||
                fallbackCopy.emailAddress.errorMessage.required,
              value: true,
            },
            validate: {
              isValid: (value: string) =>
                isValidEmail(value) ||
                localeCopy?.emailAddress?.errorMessage?.invalid ||
                fallbackCopy.emailAddress.errorMessage.invalid,
            },
          }}
        />
        <Controller
          control={control}
          name='password'
          render={({ name, onChange, value }) => (
            <Fragment>
              <Input
                additionalMessage={renderMessage({
                  errors: errors.password?.types,
                  messages:
                    localeCopy?.password?.errorMessage ||
                    fallbackCopy.password.errorMessage,
                })}
                css={{ label: { marginBottom: 5 } }}
                isError={!isEmpty(errors.password)}
                label={
                  localeCopy?.password?.label || fallbackCopy.password.label
                }
                name={name}
                onChange={onChange}
                placeholder={
                  localeCopy?.password?.placeholder ||
                  fallbackCopy.password.placeholder
                }
                type='password'
                value={value}
              />
            </Fragment>
          )}
          rules={{
            required: { message: 'is required', value: true },
            validate: {
              eightChars: (value: string) =>
                value.length > 7 ||
                localeCopy?.password?.errorMessage?.eightChars ||
                fallbackCopy.password.errorMessage.eightChars,
              oneNumber: (value: string) =>
                /.*[0-9!-/:-@[-\x60{-~].*/.test(value) ||
                //One number or symbol
                localeCopy?.password?.errorMessage?.oneNumber ||
                fallbackCopy.password.errorMessage.oneNumber,
              oneLowerCaseLetter: (value: string) =>
                /.*[a-z].*/.test(value) ||
                localeCopy?.password?.errorMessage?.oneLowerCaseLetter ||
                fallbackCopy.password.errorMessage.oneLowerCaseLetter,
              oneUpperCaseLetter: (value: string) =>
                /.*[A-Z].*/.test(value) ||
                localeCopy?.password?.errorMessage?.oneUpperCaseLetter ||
                fallbackCopy.password.errorMessage.oneUpperCaseLetter,
            },
          }}
        />
        <Controller
          control={control}
          name='over18'
          render={({ name, onChange, value }) => (
            <Checkbox
              errorMessage={errors && errors.over18?.message}
              id='over18'
              label={
                localeCopy?.checkboxes?.over18?.label ||
                fallbackCopy.checkboxes.over18.label
              }
              name={name}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleChange(event, onChange)
              }
              value={value}
            />
          )}
          rules={{
            required: {
              message:
                localeCopy?.checkboxes?.over18?.errorMessage ||
                fallbackCopy.checkboxes.over18.errorMessage,
              value: true,
            },
          }}
        />
        <Controller
          control={control}
          name='termsAccepted'
          render={({ name, onChange, value }) => (
            <Checkbox
              errorMessage={errors && errors.termsAccepted?.message}
              id='termsAccepted'
              label={
                localeCopy?.checkboxes?.termsAccepted?.label ||
                fallbackCopy.checkboxes.termsAccepted.label
              }
              name={name}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleChange(event, onChange)
              }
              value={value}
            />
          )}
          rules={{
            required: {
              message:
                localeCopy?.checkboxes?.termsAccepted?.errorMessage ||
                fallbackCopy.checkboxes.termsAccepted.errorMessage,
              value: true,
            },
          }}
        />
        <Controller
          control={control}
          name='marketingAccepted'
          render={({ name, onChange, value }) => (
            <Checkbox
              css={{
                marginBottom: 40,
                [theme.mq.desktop]: {
                  marginBottom: 48,
                },
              }}
              id='marketingAccepted'
              label={
                localeCopy?.checkboxes?.marketingAccepted?.label ||
                fallbackCopy.checkboxes.marketingAccepted.label
              }
              name={name}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleChange(event, onChange)
              }
              value={value}
            />
          )}
        />
        <Button
          css={{
            marginBottom: 40,
            [theme.mq.desktop]: {
              marginBottom: 48,
            },
          }}
          isBusy={userSigninIsLoading || userSignupIsLoading}
          isBusyText={
            localeCopy?.components?.button?.loading ||
            fallbackCopy?.components.button.loading
          }
          type='submit'
          variant='primary'
          onKeyUp={(e: any) => {
            if (e.key === 'Enter') {
              handleSubmit(onSubmit, onError).apply(this);
            }
          }}>
          {localeCopy?.submitButton || fallbackCopy?.submitButton}
        </Button>
      </Form>
      <Text
        alignSelf='flex-start'
        color='muted'
        css={{
          fontSize: 14,
        }}
        weight='light'>
        {localeCopy?.personalInformation || fallbackCopy.personalInformation}
      </Text>
      <List
        as='ul'
        css={{
          alignSelf: 'flex-start',
          marginBottom: 0,
          marginTop: 5,
          paddingLeft: 20,
          [theme.mq.desktop]: {
            paddingLeft: 40,
          },
        }}>
        {(
          localeCopy?.personalInformationList ||
          fallbackCopy.personalInformationList
        ).map((item: string, index: string) => (
          <Text
            as='li'
            color='muted'
            css={{
              marginBottom: 5,
              marginTop: 5,
            }}
            key={index}
            vairant='listItem'
            weight='light'>
            {item}
          </Text>
        ))}
      </List>
    </Page>
  );
};

export default UserRegistration;
